import type { KickerId, Locale } from '@gazette/shared';
import { t, type MessageKey } from '../i18n/index.ts';
import { BRASS_INK, INK, INK_SOFT, MANILA, MANILA_INK, MANILA_RULE, NEWSPRINT, THREAD, VIOLET } from './palette.ts';
import { portraitCanvas } from './portrait.ts';
import { fonts, inkWear, paperGrain, wrapText } from './text.ts';

// Canvas textures for the objects pinned to the board. Sizes are in texture pixels; the 3D
// layer maps them onto planes whose aspect matches `width / height`.

export interface CardArt {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

function makeCanvas(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return [canvas, canvas.getContext('2d')!];
}

export const FICHE_SIZE = { width: 512, height: 640 };

/** A Bertillon-style card: case stamp, halftone profile, typed name. */
export function drawFiche(input: { name: string; portrait: number; number: number; code: string | null; locale: Locale }): CardArt {
  const { width, height } = FICHE_SIZE;
  const [canvas, ctx] = makeCanvas(width, height);
  ctx.fillStyle = MANILA;
  ctx.fillRect(0, 0, width, height);
  paperGrain(ctx, width, height, input.portrait, 1.2);

  ctx.strokeStyle = MANILA_RULE;
  ctx.lineWidth = 3;
  ctx.strokeRect(14, 14, width - 28, height - 28);
  ctx.lineWidth = 1;
  ctx.strokeRect(22, 22, width - 44, height - 44);

  // Worn on its own layer: knocking specks out of the card itself would punch holes in it.
  const [stamp, stampCtx] = makeCanvas(width, 110);
  stampCtx.translate(40, 76);
  stampCtx.rotate(-0.035);
  stampCtx.fillStyle = VIOLET;
  stampCtx.font = fonts.display(54);
  stampCtx.fillText(t('fiche.number', { n: String(input.number).padStart(2, '0') }, input.locale).toUpperCase(), 0, 0);
  inkWear(stampCtx, width, 110, input.portrait + 1, 0.35);
  ctx.drawImage(stamp, 0, 0);

  if (input.code) {
    ctx.fillStyle = MANILA_INK;
    ctx.font = fonts.type(22);
    ctx.textAlign = 'right';
    ctx.fillText(t('fiche.case', { code: input.code }, input.locale), width - 40, 70);
    ctx.textAlign = 'left';
  }

  const photoWidth = 250;
  const photoHeight = 312;
  ctx.drawImage(portraitCanvas(input.portrait, photoWidth, photoHeight), 40, 104);

  // The printed anthropometric grid of a Bertillon card: labelled, numbered cases left blank.
  const gridX = 312;
  const gridW = width - gridX - 40;
  const rowH = photoHeight / 5;
  ctx.strokeStyle = MANILA_RULE;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(gridX, 104, gridW, photoHeight);
  for (let row = 0; row < 5; row++) {
    const y = 104 + row * rowH;
    if (row > 0) {
      ctx.beginPath();
      ctx.moveTo(gridX, y);
      ctx.lineTo(gridX + gridW, y);
      ctx.stroke();
    }
    ctx.fillStyle = MANILA_INK;
    ctx.font = fonts.body(17, 600);
    ctx.fillText(t(`fiche.m${row + 1}` as MessageKey, undefined, input.locale), gridX + 10, y + 26);
    const box = 30;
    ctx.strokeRect(gridX + gridW - box - 10, y + rowH - box - 10, box, box);
    ctx.font = fonts.type(15, 700);
    ctx.fillText(String(row + 1), gridX + 10, y + rowH - 14);
  }

  ctx.fillStyle = MANILA_INK;
  ctx.font = fonts.body(20, 700);
  ctx.fillText(t('fiche.name', undefined, input.locale).toUpperCase(), 44, 530);
  ctx.strokeStyle = MANILA_RULE;
  ctx.setLineDash([3, 5]);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(44, 588);
  ctx.lineTo(width - 44, 588);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.fillStyle = INK;
  let size = 50;
  ctx.font = fonts.type(size, 700);
  while (ctx.measureText(input.name).width > width - 100 && size > 24) {
    size -= 2;
    ctx.font = fonts.type(size, 700);
  }
  ctx.fillText(input.name, 50, 580);
  return { canvas, width, height };
}

export const CLIPPING_WIDTH = 640;

export function headlineText(kicker: KickerId, headline: string, locale: Locale): string {
  return `${t(`kicker.${kicker}`, undefined, locale).toUpperCase()} — ${headline}`;
}

/** An article cut out of the gazette, sized to its own text. */
export function drawClipping(input: { number: number; kicker: KickerId; headline: string; subtitle: string; locale: Locale; seed: number }): CardArt {
  const width = CLIPPING_WIDTH;
  const pad = 40;
  const measure = makeCanvas(1, 1)[1];
  measure.font = fonts.news(46, 800);
  const headLines = wrapText(measure, headlineText(input.kicker, input.headline, input.locale), width - pad * 2);
  measure.font = fonts.news(27, 400, true);
  const subLines = input.subtitle ? wrapText(measure, input.subtitle, width - pad * 2) : [];
  const height = Math.round(78 + headLines.length * 52 + (subLines.length ? 18 + subLines.length * 34 : 0) + 42);

  const [canvas, ctx] = makeCanvas(width, height);
  ctx.fillStyle = NEWSPRINT;
  ctx.fillRect(0, 0, width, height);
  paperGrain(ctx, width, height, input.seed, 0.8);

  ctx.fillStyle = INK_SOFT;
  ctx.font = fonts.news(20, 400, true);
  ctx.fillText('La Gazette', pad, 44);
  ctx.textAlign = 'right';
  ctx.font = fonts.news(20, 700);
  ctx.fillText(t('gz.edition', { n: input.number }, input.locale), width - pad, 44);
  ctx.textAlign = 'left';
  ctx.fillStyle = INK;
  ctx.fillRect(pad, 56, width - pad * 2, 2);

  let y = 78 + 38;
  ctx.font = fonts.news(46, 800);
  for (const line of headLines) {
    ctx.fillText(line, pad, y);
    y += 52;
  }
  if (subLines.length) {
    y += 8;
    ctx.fillStyle = INK_SOFT;
    ctx.font = fonts.news(27, 400, true);
    for (const line of subLines) {
      ctx.fillText(line, pad, y);
      y += 34;
    }
  }
  return { canvas, width, height };
}

export type StampWord = 'true' | 'false' | 'author' | 'plume';

const STAMP_LABEL: Record<StampWord, MessageKey> = {
  true: 'rev.true',
  false: 'rev.false',
  author: 'rev.author.stamp',
  plume: 'title.plume',
};

/** Rubber-stamp impression on a transparent ground: red for verdicts, brass for the Golden Quill. */
export function drawStamp(word: StampWord, locale: Locale, seed: number): CardArt {
  const width = 560;
  const height = 220;
  const [canvas, ctx] = makeCanvas(width, height);
  const label = t(STAMP_LABEL[word], undefined, locale).toUpperCase();
  const ink = word === 'plume' ? BRASS_INK : THREAD;
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;
  ctx.lineWidth = 10;
  ctx.strokeRect(12, 12, width - 24, height - 24);
  ctx.lineWidth = 3;
  ctx.strokeRect(30, 30, width - 60, height - 60);
  let size = 170;
  ctx.font = fonts.display(size);
  while (ctx.measureText(label).width > width - 110) {
    size -= 6;
    ctx.font = fonts.display(size);
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, width / 2, height / 2 + size * 0.04);
  inkWear(ctx, width, height, seed, 0.9);
  return { canvas, width, height };
}
